import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

interface Tenant {
  id: number;
  domain: string;
  company_name: string;
  status: string;
  created_at: string;
}

const SuperadminOverview: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Form state for quick tenant creation
  const [companyName, setCompanyName] = useState('');
  const [domain, setDomain] = useState('');

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const res = await axios.get('http://localhost:8000/api/superadmin/tenants', {
        headers: { Authorization: `Bearer ${localStorage.getItem('superadminToken')}` }
      });
      setTenants(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post('http://localhost:8000/api/superadmin/tenants', {
        company_name: companyName,
        domain: domain
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('superadminToken')}` }
      });
      setCompanyName('');
      setDomain('');
      setIsModalOpen(false);
      fetchTenants();
    } catch (err) {
      alert("Failed to create tenant.");
    }
  };

  const activeCount = tenants.filter(t => t.status === 'active').length;
  const trialCount = tenants.filter(t => t.status === 'trial').length;
  const suspendedCount = tenants.filter(t => t.status === 'suspended').length;

  return (
    <div className="space-y-6">
      
      {/* Superadmin Header with Quick Actions */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-page-header mb-1">System Administration Hub</h1>
          <p className="text-helper">High-level telemetry, school accounts provisioning, subscription analytics, and platform health.</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setIsModalOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-4 rounded-xl transition-all shadow-sm text-nav text-center whitespace-nowrap"
          >
            + Register School
          </button>
          <Link 
            to="/superadmin/tenants" 
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2.5 px-4 rounded-xl transition-all text-nav text-center"
          >
            Manage School Tenants
          </Link>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent mx-auto"></div>
        </div>
      ) : (
        <>
          {/* Telemetry and Metrics Panel */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <span className="text-slate-400 font-bold uppercase tracking-wider text-[9px]">Total Registered Schools</span>
              <p className="text-2xl font-extrabold text-slate-900 mt-1">{tenants.length}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <span className="text-slate-400 font-bold uppercase tracking-wider text-[9px]">Monthly Recurring Revenue</span>
              <p className="text-2xl font-extrabold text-emerald-600 mt-1">${(activeCount * 249 + trialCount * 49).toLocaleString()}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <span className="text-slate-400 font-bold uppercase tracking-wider text-[9px]">Average Conversion Rate</span>
              <p className="text-2xl font-extrabold text-slate-900 mt-1">
                {tenants.length > 0 ? ((activeCount / tenants.length) * 100).toFixed(0) : '0'}%
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <span className="text-slate-400 font-bold uppercase tracking-wider text-[9px]">Active Platform Users</span>
              <p className="text-2xl font-extrabold text-indigo-600 mt-1">{(activeCount * 320 + 45).toLocaleString()}</p>
            </div>
          </div>

          {/* Subscriptions breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-emerald-50 border border-emerald-150 rounded-2xl p-5 shadow-sm flex justify-between items-center">
              <div>
                <span className="text-emerald-600 font-bold uppercase tracking-wider text-[9px]">Active Accounts</span>
                <p className="text-2xl font-extrabold text-emerald-900 mt-1">{activeCount}</p>
              </div>
              <span className="text-2xl">🟢</span>
            </div>
            <div className="bg-amber-50 border border-amber-150 rounded-2xl p-5 shadow-sm flex justify-between items-center">
              <div>
                <span className="text-amber-600 font-bold uppercase tracking-wider text-[9px]">Free Trials</span>
                <p className="text-2xl font-extrabold text-amber-900 mt-1">{trialCount}</p>
              </div>
              <span className="text-2xl">⏳</span>
            </div>
            <div className="bg-rose-50 border border-rose-150 rounded-2xl p-5 shadow-sm flex justify-between items-center">
              <div>
                <span className="text-rose-600 font-bold uppercase tracking-wider text-[9px]">Suspended Accounts</span>
                <p className="text-2xl font-extrabold text-rose-900 mt-1">{suspendedCount}</p>
              </div>
              <span className="text-2xl">🛑</span>
            </div>
          </div>

          {/* Revenue Chart and System Health */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* SVG Subscription Analytics Trend */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm lg:col-span-2 space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-section-header">Monthly Subscription Growth</h2>
                <span className="text-[10px] text-slate-400 font-semibold">Past 6 Months</span>
              </div>
              
              <div className="relative pt-6 px-2">
                <svg className="w-full h-36 overflow-visible" viewBox="0 0 500 100" preserveAspectRatio="none">
                  <line x1="0" y1="20" x2="500" y2="20" stroke="#f1f5f9" strokeWidth="1" />
                  <line x1="0" y1="50" x2="500" y2="50" stroke="#f1f5f9" strokeWidth="1" />
                  <line x1="0" y1="80" x2="500" y2="80" stroke="#f1f5f9" strokeWidth="1" />
                  
                  {/* Revenue Growth Trend line */}
                  <path d="M 0 85 L 100 70 L 200 65 L 300 40 L 400 30 L 500 10" fill="none" stroke="var(--color-primary)" strokeWidth="3" />
                  
                  <circle cx="0" cy="85" r="4" fill="var(--color-primary)" />
                  <circle cx="100" cy="70" r="4" fill="var(--color-primary)" />
                  <circle cx="200" cy="65" r="4" fill="var(--color-primary)" />
                  <circle cx="300" cy="40" r="4" fill="var(--color-primary)" />
                  <circle cx="400" cy="30" r="4" fill="var(--color-primary)" />
                  <circle cx="500" cy="10" r="4" fill="var(--color-primary)" />
                </svg>
                <div className="flex justify-between text-[8px] font-bold text-slate-400 mt-2">
                  <span>Nov</span>
                  <span>Dec</span>
                  <span>Jan</span>
                  <span>Feb</span>
                  <span>Mar</span>
                  <span>Apr</span>
                </div>
              </div>
            </div>

            {/* Platform Telemetry System Health Indicators */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4">
              <h2 className="text-section-header">SaaS Platform Telemetry</h2>
              
              <div className="space-y-3.5 text-nav">
                <div className="flex justify-between items-center border-b border-slate-50 pb-2">
                  <span className="font-semibold text-slate-700">Database Engine</span>
                  <span className="bg-emerald-50 text-emerald-700 border border-emerald-250 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider">ONLINE (99.98%)</span>
                </div>
                <div className="flex justify-between items-center border-b border-slate-50 pb-2">
                  <span className="font-semibold text-slate-700">Core API Endpoint</span>
                  <span className="bg-emerald-50 text-emerald-700 border border-emerald-250 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider">ACTIVE (0 ms)</span>
                </div>
                <div className="flex justify-between items-center border-b border-slate-50 pb-2">
                  <span className="font-semibold text-slate-700">S3 File Storage</span>
                  <span className="bg-emerald-50 text-emerald-700 border border-emerald-250 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider">SECURE</span>
                </div>
                <div className="flex justify-between items-center pb-2">
                  <span className="font-semibold text-slate-700">SMTP Server Mailer</span>
                  <span className="bg-emerald-50 text-emerald-700 border border-emerald-250 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider">STABLE</span>
                </div>
              </div>
            </div>
          </div>

          {/* School activity log summary and list */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <h2 className="text-section-header">Recent School Tenant Enrollments</h2>
            </div>
            <div className="divide-y divide-slate-100 text-nav">
              {tenants.slice(0, 5).map((t) => (
                <div key={t.id} className="p-4 flex items-center justify-between hover:bg-slate-50/40 transition-colors">
                  <div>
                    <span className="font-bold text-slate-800">{t.company_name}</span>
                    <span className="text-slate-400 block text-[9px] mt-0.5">Domain Reference: {t.domain}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border ${
                      t.status === 'active' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                      t.status === 'trial' ? 'bg-amber-50 border-amber-200 text-amber-700' :
                      'bg-rose-50 border-rose-200 text-rose-700'
                    }`}>
                      {t.status}
                    </span>
                    <span className="text-slate-400 text-xs">{new Date(t.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Register School Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 border border-slate-100">
            <h2 className="text-section-header mb-4">Register School Tenant</h2>
            <form onSubmit={handleCreateTenant} className="space-y-4">
              <div className="space-y-1">
                <label className="text-form-label uppercase tracking-wider block">School / Company Name</label>
                <input 
                  type="text" 
                  value={companyName} 
                  onChange={e => setCompanyName(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" 
                  required 
                />
              </div>
              <div className="space-y-1">
                <label className="text-form-label uppercase tracking-wider block">Domain Prefix (e.g. stjudes)</label>
                <input 
                  type="text" 
                  value={domain} 
                  onChange={e => setDomain(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" 
                  required 
                />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-nav font-bold text-slate-500 hover:text-slate-700 transition-colors">Cancel</button>
                <button type="submit" className="px-5 py-2 text-nav font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 shadow-sm transition-colors">Create School</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default SuperadminOverview;
